import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

/* ===========================================================================
 *  Eén veld aanpassen, als blad van onderen (20 okt 2026)
 *
 *  Casper: "wat ik op telefoon wel echt mis, is dat ik dingen zou kunnen
 *  aanpassen op een fijne manier." Dus: tik op een waarde, en dit blad komt
 *  omhoog met het goede toetsenbord -- cijfers voor een bedrag, de
 *  datumkiezer van de telefoon voor een datum -- en één knop. Enter slaat op.
 *
 *  Kiezen uit een lijst (grootboek, conditie, onderneming) gaat niet hier
 *  maar via de Kiezer uit ui.tsx: die kan zoeken en is al bekend.
 * =========================================================================== */

export type Soort = 'tekst' | 'bedrag' | 'datum' | 'iban'

const twee = (n: number) => String(n).padStart(2, '0')

/** ms naar jjjj-mm-dd voor <input type="date">, in de eigen tijdzone (zoals het dashboard). */
export function naarDatumVeld(ms: number | null | undefined): string {
  if (!ms) return ''
  const d = new Date(ms)
  return `${d.getFullYear()}-${twee(d.getMonth() + 1)}-${twee(d.getDate())}`
}

/** Terug: middernacht van die dag, lokaal -- dayFromDateInput in het dashboard. */
export function uitDatumVeld(v: string): number | null {
  if (!v) return null
  const [j, m, d] = v.split('-').map(Number)
  if (!j || !m || !d) return null
  return new Date(j, m - 1, d, 0, 0, 0, 0).getTime()
}

/** "1.234,50" of "1234.5" of "-12,3" naar een getal; null als het niets is. */
export function leesBedrag(v: string): number | null {
  const t = v.replace(/\s|€/g, '')
  if (!t) return null
  /* Met een komma erin is de punt een duizendtal; zonder komma is de punt decimaal. */
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t)
  return Number.isFinite(n) ? n : NaN
}

export const bedragVeld = (n: number | null | undefined) =>
  n == null ? '' : n.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false })

export function Bewerker({ titel, uitleg, soort, begin, leegMag, sluit, bewaar }: {
  titel: string
  uitleg?: string
  soort: Soort
  /** Wat er nu in het veld staat, al in de vorm van het invoerveld. */
  begin: string
  leegMag?: boolean
  sluit: () => void
  /** De ingevoerde tekst; gooit met een melding als het niet goed is. */
  bewaar: (waarde: string) => Promise<void>
}) {
  const [waarde, setWaarde] = useState(begin)
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)
  const veld = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const v = veld.current
    if (!v) return
    v.focus()
    /* Alles geselecteerd: wie een bedrag aanpast, typt meestal een nieuw. */
    if (soort !== 'datum') v.select()
  }, [soort])

  const leeg = !waarde.trim()
  const ongewijzigd = waarde.trim() === begin.trim()

  async function opslaan() {
    if (bezig || ongewijzigd || (leeg && !leegMag)) return
    if (soort === 'bedrag' && !leeg && Number.isNaN(leesBedrag(waarde))) {
      setFout('Dat is geen bedrag. Gebruik alleen cijfers en een komma.')
      return
    }
    setBezig(true)
    setFout(null)
    try {
      await bewaar(waarde.trim())
      sluit()
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    } finally {
      setBezig(false)
    }
  }

  return (
    <div className="vraag-achter" onClick={() => !bezig && sluit()}>
      <form className="vraag" role="dialog" aria-label={titel} onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); void opslaan() }}>
        <div className="vraag-kop">
          <h2>{titel}</h2>
          <button type="button" className="kop-knop" onClick={sluit} disabled={bezig} aria-label="Sluiten"><X size={22} /></button>
        </div>
        {uitleg && <div className="vraag-uitleg">{uitleg}</div>}
        <input
          ref={veld}
          className={`bewerk-veld ${soort === 'bedrag' ? 'rechts' : ''}`}
          type={soort === 'datum' ? 'date' : 'text'}
          inputMode={soort === 'bedrag' ? 'decimal' : undefined}
          autoCapitalize={soort === 'iban' ? 'characters' : 'sentences'}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={soort === 'tekst'}
          enterKeyHint="done"
          value={waarde}
          onChange={(e) => { setWaarde(e.target.value); setFout(null) }}
        />
        {fout && <div className="bewerk-fout">{fout}</div>}
        <button type="submit" className="knop breed blauw" disabled={bezig || ongewijzigd || (leeg && !leegMag)}>
          {bezig ? 'Bezig…' : 'Opslaan'}
        </button>
        {leegMag && !leeg && (
          <button type="button" className="knop breed lucht" disabled={bezig} onClick={() => setWaarde('')}>Leegmaken</button>
        )}
      </form>
    </div>
  )
}

/** Btw: het percentage met drie knoppen, en desgewenst het bedrag zoals het op de factuur staat. */
export function BtwBewerker({ pct, bedrag, sluit, bewaar }: {
  pct: number | null
  bedrag: number | null
  sluit: () => void
  bewaar: (pct: number, bedrag: number | null) => Promise<void>
}) {
  const [p, setP] = useState<number>(pct ?? 21)
  const [b, setB] = useState(bedragVeld(bedrag))
  const [bezig, setBezig] = useState(false)
  const [fout, setFout] = useState<string | null>(null)

  async function opslaan() {
    const n = leesBedrag(b)
    if (Number.isNaN(n)) { setFout('Dat is geen bedrag. Gebruik alleen cijfers en een komma.'); return }
    setBezig(true)
    setFout(null)
    try {
      await bewaar(p, n)
      sluit()
    } catch (e) {
      setFout(e instanceof Error ? e.message : String(e))
    } finally {
      setBezig(false)
    }
  }

  return (
    <div className="vraag-achter" onClick={() => !bezig && sluit()}>
      <form className="vraag" role="dialog" aria-label="Btw" onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); void opslaan() }}>
        <div className="vraag-kop">
          <h2>Btw</h2>
          <button type="button" className="kop-knop" onClick={sluit} disabled={bezig} aria-label="Sluiten"><X size={22} /></button>
        </div>
        <div className="btw-keus" role="radiogroup" aria-label="Percentage">
          {[21, 9, 0].map((x) => (
            <button key={x} type="button" role="radio" aria-checked={p === x} className={p === x ? 'aan' : ''} onClick={() => setP(x)}>
              {x}%
            </button>
          ))}
        </div>
        <label className="veld-label">Btw-bedrag zoals op de factuur (mag leeg; dan rekenen we het uit)</label>
        <input className="bewerk-veld rechts" type="text" inputMode="decimal" autoComplete="off" enterKeyHint="done"
          value={b} onChange={(e) => { setB(e.target.value); setFout(null) }} />
        {fout && <div className="bewerk-fout">{fout}</div>}
        <button type="submit" className="knop breed blauw" disabled={bezig}>{bezig ? 'Bezig…' : 'Opslaan'}</button>
      </form>
    </div>
  )
}
